<template>
  <Dialog
    :visible="shouldDelete"
    :draggable="false"
    header="Confirm Deletion"
    :style="{ width: '60vw' }"
    :closable="false"
    modal
  >
    <p>Are you sure you want to delete this scenario?</p>
    <ul class="font-semibold">
      <li>{{ scenarioForm.initialData.value?.title }}</li>
    </ul>

    <p v-if="perfevals.length">
      The following performance evaluations will also be deleted
    </p>
    <ul class="font-semibold">
      <li v-for="perfeval in perfevals" :key="perfeval.id">
        {{ perfeval.title }} ({{ perfeval.r_author?.email }})
      </li>
    </ul>

    <template #footer>
      <Button
        label="Cancel"
        class="p-button-text"
        @click="emit('update:shouldDelete', false)"
      />
      <Button
        label="Delete"
        class="p-button-danger"
        :disabled="isSubmitting"
        @click="handleDeleteScenario"
      />
    </template>
  </Dialog>
</template>

<script lang="ts" setup>
import { haltUntilCompletion } from "@/composables/exchanges/mutationLimiter";
import { useMutationNotification } from "@/composables/logger/mutationNotification";
import {
  perfevalsKey,
  scenarioFormKey,
  sizingFormKey
} from "@/composables/scenarioDetailProvider/scenarioDetailProviderKeys";
import { injectStrict, omit } from "@/utils/utils";
import {
  useInsertScenarioRevisionMutation,
  useInsertSizingsRevisionMutation
} from "gql/api";
import { ref } from "vue";
import { useRouter } from "vue-router";

defineProps<{ shouldDelete: boolean }>();
const emit = defineEmits<{
  (e: "update:shouldDelete", value: boolean): void;
}>();

const sizingForm = injectStrict(sizingFormKey);
const scenarioForm = injectStrict(scenarioFormKey);
const perfevals = injectStrict(perfevalsKey);
const router = useRouter();
const isSubmitting = ref(false);
const { registerResultToast } = useMutationNotification();

const { executeMutation: insertScenarioRevision } =
  useInsertScenarioRevisionMutation();

const { executeMutation: insertSizingRevision } =
  useInsertSizingsRevisionMutation();

async function handleDeleteScenario() {
  const isLastScenario = ref(false);
  isLastScenario.value =
    (sizingForm.data.value?.scenarios &&
      sizingForm.data.value?.scenarios.length === 1) ||
    false;

  if (isSubmitting.value) return;
  isSubmitting.value = true;
  scenarioForm.reset();
  scenarioForm.update({
    journal_deleted_at: "now()"
  });
  // nullify installed scenario if deleted
  if (
    sizingForm.data.value.installed_scenario_id === scenarioForm.data.value.id
  ) {
    sizingForm.reset();
    sizingForm.update({ installed_scenario_id: null });
    const ignoredSizingFields = [
      "parent_sizing",
      "author",
      "well",
      "customer",
      "scenarios"
    ];
    const sizingRevision = {
      ...omit(sizingForm.prepare(), (_, k) => ignoredSizingFields.includes(k))
    };
    insertSizingRevision({ objects: [sizingRevision] });
  }

  const ignoredScenarioFields = [
    "author",
    "correlations",
    "equipment",
    "parameters",
    "well_properties"
  ];

  const scenarioRevision = {
    ...omit(scenarioForm.prepare(), (_, k) => ignoredScenarioFields.includes(k))
  };

  const result = insertScenarioRevision({ objects: [scenarioRevision] });
  registerResultToast("Scenario Delete", result);
  await haltUntilCompletion();
  emit("update:shouldDelete", false);

  const params: { sizingId: string; scenarioId?: string } = {
    sizingId: sizingForm.data.value.id
  };

  await router.push({
    name: "Sizing",
    params,
    query: { deletedScenario: "true" }
  });

  isSubmitting.value = false;
}
</script>
